/**
 * Shared South African MSISDN normalization + saved-contact matching helpers.
 *
 * These are used by both Send Money and Request Money so the "is this person
 * already a saved contact?" check behaves identically and is immune to the
 * phone-format differences that previously caused the duplicate
 * "save beneficiary?" prompt (e.g. "+27821234567" vs "27821234567" vs "0821234567").
 */

export type SavedContactLike = {
  identifier?: string;
  msisdn?: string;
  accountType?: string;
  accounts?: Array<{ type?: string; identifier?: string }>;
};

/**
 * Reduce any SA mobile number to a canonical "27XXXXXXXXX" form for comparison.
 * Returns '' when there are no usable digits.
 */
export function normalizeSAMsisdn(value?: string | null): string {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  // Already in 27XXXXXXXXX form
  if (digits.startsWith('27') && digits.length === 11) return digits;
  // Local 0XXXXXXXXX form
  if (digits.startsWith('0') && digits.length === 10) return '27' + digits.slice(1);
  // Bare 9-digit subscriber number (e.g. 821234567)
  if (digits.length === 9) return '27' + digits;
  // Fallback: if it looks like it has a country code already, keep last 11 27-digits
  if (digits.startsWith('27')) return '27' + digits.slice(-9);
  return digits;
}

/**
 * Compare two SA mobile numbers regardless of +27 / 27 / 0 / spacing differences.
 */
export function msisdnEquals(a?: string | null, b?: string | null): boolean {
  const na = normalizeSAMsisdn(a);
  const nb = normalizeSAMsisdn(b);
  return !!na && na === nb;
}

/**
 * Keep only digits (used for bank account number comparison).
 */
export function digitsOnly(value?: string | null): string {
  return String(value || '').replace(/\D/g, '');
}

/**
 * True when the saved contact represents the given MyMoolah wallet MSISDN.
 * Checks the top-level identifier/msisdn AND any linked mymoolah service accounts,
 * all via normalized comparison.
 */
export function contactMatchesMsisdn(contact: SavedContactLike, msisdn?: string | null): boolean {
  const target = normalizeSAMsisdn(msisdn);
  if (!target) return false;

  if (msisdnEquals(contact.identifier, msisdn)) return true;
  if (msisdnEquals(contact.msisdn, msisdn)) return true;

  const accounts = contact.accounts || [];
  return accounts.some(
    (acc) =>
      (acc?.type === 'mymoolah' || acc?.type === undefined) &&
      msisdnEquals(acc?.identifier, msisdn)
  );
}

/**
 * True when the saved contact has a bank account matching the given account number.
 * Bank account numbers are compared on digits only.
 */
export function contactMatchesBankAccount(
  contact: SavedContactLike,
  accountNumber?: string | null
): boolean {
  const target = digitsOnly(accountNumber);
  if (!target) return false;

  if (contact.accountType === 'bank' && digitsOnly(contact.identifier) === target) return true;

  const accounts = contact.accounts || [];
  return accounts.some(
    (acc) =>
      (acc?.type === 'bank' || acc?.type === 'eft' || acc?.type === 'payshap') &&
      digitsOnly(acc?.identifier) === target
  );
}

/**
 * Find a saved MyMoolah contact in a list by MSISDN (normalized), or undefined.
 */
export function findContactByMsisdn<T extends SavedContactLike>(
  contacts: T[],
  msisdn?: string | null
): T | undefined {
  return (contacts || []).find((c) => contactMatchesMsisdn(c, msisdn));
}

/**
 * Find a saved bank contact in a list by account number (digits), or undefined.
 */
export function findContactByBankAccount<T extends SavedContactLike>(
  contacts: T[],
  accountNumber?: string | null
): T | undefined {
  return (contacts || []).find((c) => contactMatchesBankAccount(c, accountNumber));
}
